import { useNavigate } from "react-router-dom";
import { Bell, BellOff, Megaphone, Calendar, CreditCard, FileText, Megaphone as Announce, type LucideIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { useNotifications, type NotificationItem } from "@/hooks/useNotifications";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDistanceToNow } from "date-fns";

const TYPE_ICON: Record<NotificationItem["type"], LucideIcon> = {
  campaign_status: Megaphone,
  booking_status: Calendar,
  payment: CreditCard,
  invoice: FileText,
  announcement: Announce,
  system: Bell,
};

interface Props {
  trigger: React.ReactNode;
}

export const NotificationsPopover = ({ trigger }: Props) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { items, unreadCount, markRead, markAllRead, loading } = useNotifications();
  const isAr = language === "ar";

  const handleClick = (n: NotificationItem) => {
    if (!n.read) markRead(n.id);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={12}
        className="w-[380px] p-0 rounded-2xl border-border/70 shadow-2xl shadow-primary/10 z-[9999] overflow-hidden bg-background"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">
              {isAr ? "الإشعارات" : "Notifications"}
            </span>
            {unreadCount > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">
                {unreadCount}
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-xs text-primary hover:text-primary/80 font-medium"
            >
              {isAr ? "تعليم الكل كمقروء" : "Mark all read"}
            </button>
          )}
        </div>

        <ScrollArea className="h-[420px]">
          {!user ? (
            <EmptyState
              title={isAr ? "سجّل الدخول" : "Sign in"}
              body={isAr ? "سجّل الدخول لرؤية إشعاراتك." : "Sign in to see your notifications."}
              cta={isAr ? "تسجيل الدخول" : "Sign in"}
              onCta={() => navigate("/auth")}
            />
          ) : loading ? (
            <div className="px-6 py-12 text-center text-sm text-muted-foreground">
              {isAr ? "جارٍ التحميل…" : "Loading…"}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={BellOff}
              title={isAr ? "لا توجد إشعارات" : "No notifications"}
              body={isAr ? "ستظهر التحديثات هنا." : "Updates will show up here."}
            />
          ) : (
            <ul className="divide-y divide-border/50">
              {items.map((n) => {
                const Icon = TYPE_ICON[n.type] ?? Bell;
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => handleClick(n)}
                      className={`w-full text-left flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/60 ${
                        !n.read ? "bg-primary/[0.04]" : ""
                      }`}
                    >
                      <span className="mt-0.5 h-9 w-9 rounded-lg bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center shrink-0 ring-1 ring-primary/15">
                        <Icon className="h-[18px] w-[18px] text-primary" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground truncate">
                            {n.title}
                          </span>
                          {!n.read && (
                            <span className="h-2 w-2 rounded-full bg-primary shrink-0" />
                          )}
                        </div>
                        {n.body && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {n.body}
                          </p>
                        )}
                        <span className="text-[10px] text-muted-foreground/80 mt-1 block">
                          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

const EmptyState = ({
  icon: Icon = Bell,
  title,
  body,
  cta,
  onCta,
}: {
  icon?: LucideIcon;
  title: string;
  body: string;
  cta?: string;
  onCta?: () => void;
}) => (
  <div className="px-6 py-12 text-center flex flex-col items-center gap-3">
    <div className="h-12 w-12 rounded-full bg-muted/60 flex items-center justify-center">
      <Icon className="h-5 w-5 text-muted-foreground" />
    </div>
    <div>
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-xs text-muted-foreground mt-1">{body}</div>
    </div>
    {cta && onCta && (
      <Button size="sm" onClick={onCta} className="rounded-full mt-1">
        {cta}
      </Button>
    )}
  </div>
);
